import React, { useState, useEffect } from 'react';
import PhotoGalleryUpload from './Upload';

import { ImageError, ImageResult } from '../../../helpers/images/mediaApiUtils';
import { getFileKey, useImageUploader, validateImage } from '../../../helpers/images/useImageUploader';
import { imageFileToResizedDataUrl, loadDataUrlToImg } from '../../../helpers/images/processImages';
import { useManageItem } from '../../../helpers/useManageItem';
import {
  PhotoItemsFragment,
  usePhotoGalleryQuery,
  useUpdatePhotoGalleryItemMutation,
} from '../../../generated/graphql';
import PhotoGalleryEdit from './Edit';
import { PageItemProps } from '../BasicItem';

export interface PhotoUploading {
  file: File;
  key: string;
  percentUploaded: number;
  dataUrl: string;
  error?: ImageError;
  mediaResponse?: ImageResult;
}

interface UpdatePhotoProps {
  key: string;
  updates: any;
}
const PhotoGallery = ({ onSaveHandler }: PageItemProps) => {
  const [update, { loading: updateLoading }] = useUpdatePhotoGalleryItemMutation();
  const { imageUpload } = useImageUploader();
  const { itemId: id, pageId, isNew, trackItem } = useManageItem();
  const [photoItems, setPhotoItems] = useState<PhotoItemsFragment[]>([]);
  const [photosUploading, setPhotosUploading] = useState<PhotoUploading[]>([]);
  const uploadsWithErrors = photosUploading.filter(p => p.error);
  const { data: galleryData, loading: isGalleryLoading } = usePhotoGalleryQuery({
    variables: {
      pageId,
      id: id!,
    },
    skip: isNew,
  });

  const existingPhotoItems = galleryData?.page.photoGalleryItem?.photoItems;
  useEffect(() => {
    if (existingPhotoItems) setPhotoItems(existingPhotoItems);
  }, [existingPhotoItems]);

  const updatePhotoUploading = ({ key, updates }: UpdatePhotoProps) => {
    setPhotosUploading(prevPhotosUploading => {
      const update = prevPhotosUploading?.map(photo => {
        if (photo.key !== key) return photo;
        return { ...photo, ...updates };
      });
      return update;
    });
  };

  const removePhotoUploading = (key: string) => {
    setPhotosUploading(prevPhotosUploading => {
      return prevPhotosUploading.filter(p => p.key !== key);
    });
  };

  const addUploadsOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList) {
      addUploads(Array.from(fileList));
    }
  };

  const addUploads = async (fileRefs: File[]) => {
    const existingKeys = photosUploading.map(p => p.key);
    const deduped = fileRefs.filter(f => !existingKeys.includes(getFileKey(f)));
    // Check if there are any replacements for uploads that failed
    uploadsWithErrors.forEach(errorUpload => {
      const replacementUpload = fileRefs.find(f => errorUpload.key === getFileKey(f));
      if (replacementUpload) {
        removePhotoUploading(errorUpload.key);
        deduped.unshift(replacementUpload);
      }
    });
    const toUpload = await Promise.all(
      deduped.map(async f => {
        const dataUrl = await imageFileToResizedDataUrl(f);
        const img = await loadDataUrlToImg(dataUrl);
        return {
          dataUrl,
          error: validateImage(img),
          file: f,
          key: getFileKey(f),
          percentUploaded: 0,
        };
      })
    );
    setPhotosUploading(prevPhotosUploading => [...prevPhotosUploading, ...toUpload]);
    await Promise.all(
      toUpload
        .filter(u => !u.error)
        .map(async photo => {
          const setUploadPercent = (percent: number) => {
            updatePhotoUploading({ key: photo.key, updates: { percentUploaded: percent } });
          };
          const res = await imageUpload({ file: photo.dataUrl, setUploadPercent });
          const updates = {
            error: res.error,
            mediaResponse: res.data,
          };
          updatePhotoUploading({ key: photo.key, updates });
          return { ...photo, ...updates };
        })
    );
    if (onSaveHandler) {
      onSaveHandler();
    }
  };

  return (
    <>
      {photosUploading.length > 0 ? (
        <PhotoGalleryUpload
          addUploadsOnChange={addUploadsOnChange}
          id={id}
          pageId={pageId}
          photosUploading={photosUploading}
          setPhotoItems={setPhotoItems}
          setPhotosUploading={setPhotosUploading}
          update={update}
          trackItem={trackItem}
          updateLoading={updateLoading}
          uploadsWithErrors={uploadsWithErrors}
        />
      ) : (
        <PhotoGalleryEdit
          addUploads={addUploads}
          addUploadsOnChange={addUploadsOnChange}
          isGalleryLoading={isGalleryLoading}
          id={id}
          isNew={isNew}
          pageId={pageId}
          photoItems={photoItems}
          setPhotoItems={setPhotoItems}
          update={update}
          trackItem={trackItem}
        />
      )}
    </>
  );
};

export default PhotoGallery;
